/**
 * SCRUM-1032 review — re-pointing a sub-area staged under a NEW area while
 * that area's create was out.
 *
 * Such a sub-area was not in its parent's create, and is left staged under the
 * parent's draft id, which no longer exists once the area is on the server.
 * The create answers only `1`, so the new area has to be found in the list.
 */

/** No parent: the API sends a top-level area's parentId as null, '' or 0. */
export const isTopLevelParentId = (parentId: unknown) =>
  parentId == null || String(parentId) === '' || String(parentId) === '0'

/** A new area the grid has created, remembered until its sub-areas follow. */
export interface CreatedParent {
  name: string
  /** Area ids that existed before the create: the new area is not one. */
  before: ReadonlySet<number>
}

/**
 * The server id of each created area, by its old draft id.
 *
 * Matched by name, but only among areas that did not exist before the create
 * — names are not unique, and "last match wins" could attach the sub-area to
 * an older area of the same name. Two new areas sharing a name are matched in
 * creation order (the map's order) to ascending ids. An area with no match
 * yet is left out, so the caller keeps its sub-areas staged and tries again.
 */
export const resolveCreatedParents = (
  created: ReadonlyMap<string, CreatedParent>,
  areas: ReadonlyArray<{ id: number; name: string; parentId?: unknown }>,
): Map<string, number> => {
  const taken = new Set<number>()
  const resolved = new Map<string, number>()
  created.forEach((parent, draftId) => {
    const match = areas
      .filter(
        (area) =>
          isTopLevelParentId(area.parentId) &&
          area.name === parent.name &&
          !parent.before.has(area.id) &&
          !taken.has(area.id),
      )
      .sort((a, b) => a.id - b.id)[0]
    if (!match) return
    taken.add(match.id)
    resolved.set(draftId, match.id)
  })
  return resolved
}

/**
 * Drafts after a draft is discarded: the draft itself and, when it is a new
 * area, the sub-areas staged under it. Those are never drawn without their
 * parent, and a blank one held autosave off with a "Sub-area name is
 * required" nobody could see.
 */
export const discardDraftWithChildren = <
  D extends { id: number; parentId: string },
>(
  drafts: ReadonlyArray<D>,
  id: number,
): { kept: D[]; removedIds: number[] } => {
  const removedIds: number[] = []
  const kept = drafts.filter((draft) => {
    const removed = draft.id === id || draft.parentId === String(id)
    if (removed) removedIds.push(draft.id)
    return !removed
  })
  return { kept, removedIds }
}
